import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, connect } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import './edit-username.css';
import * as SwalHandled from '../../../../shared/utils/swalHandler';
import { showManualSuccessPushToast } from '../../../../shared/pusNotification/useManualToasts';
import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import { USER_TYPE } from '../../../../shared/constants/common';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControlWithFocus,
  FormMessageWithValidation,
  useFormValidation,
  handleInputChange,
} from '../../../../shared/components/form';
import { createEditUsernameSchema } from '../../../../shared/validations/setting';
import {
  getUserStatus,
  isUserLoggedIn,
  sanitizeUsernameInput,
  validateUsername,
} from '../../../../shared/utils/function';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { 
  fetchUser, updateUser as updateUserAction, 
  deleteUser as deleteUserAction 
} from '../../../../shared/actions/userActions';
import { fetchCreator } from '../../../../shared/actions/creatorActions';
import { checkUsername as checkUsernameAction } from '../../../../shared/actions/authActions';
import { clearLocalStorage } from '../../../../shared/utils/config/localStorage';
import { clearSessionStorage } from '../../../../shared/utils/config/sessionStorage';


const DELETE_CONFIRMATION_WORD = 'delete';
const USERNAME_CHECK_DELAY = 400;

const normalizeValue = (value = '') => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().toLowerCase();
};


const EditUsername = ({
  user,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const isLoggedIn = isUserLoggedIn();
  const userStatus = getUserStatus();
  
  const currentUsername = user?.username;
  const isCreator = userStatus === USER_TYPE.CREATOR;
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const schema = useMemo(() => createEditUsernameSchema(currentUsername), [currentUsername]);
  const resolver = useMemo(() => zodResolver(schema), [schema]);

  const form = useForm({
    resolver,
    defaultValues: {
      username: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    validateAndFocus,
    shouldShowError,
  } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  const usernameValue = form.watch('username');

  useEffect(() => {
    if (!isLoggedIn) {
      navigate.toSignup();
    }
  }, [isLoggedIn, navigate]);

  // Check username availability
  useEffect(() => {
    if (!usernameValue) {
      setIsUsernameAvailable(null);
      setIsCheckingUsername(false);
      return;
    }

    const normalizedCurrent = normalizeValue(currentUsername);
    const normalizedValue = normalizeValue(usernameValue);
    const isSameUsername = normalizedValue === normalizedCurrent;

    if (isSameUsername) {
      setIsUsernameAvailable(null);
      setIsCheckingUsername(false);
      return;
    }

    const meetsMinimumRequirements = usernameValue.length >= 3 && validateUsername(usernameValue);

    if (!meetsMinimumRequirements) {
      setIsUsernameAvailable(false);
      setIsCheckingUsername(false);
      return;
    }

    let isCancelled = false;
    setIsCheckingUsername(true);

    const timeoutId = setTimeout(async () => {
      try {
        const result = await dispatch(checkUsernameAction(usernameValue));

        if (isCancelled) {
          return;
        }

        setIsUsernameAvailable(Boolean(result?.success && result.data?.isValid));
      } catch (error) {
        if (!isCancelled) {
          setIsUsernameAvailable(false);
        }
      } finally {
        if (!isCancelled) {
          setIsCheckingUsername(false);
        }
      }
    }, USERNAME_CHECK_DELAY);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [usernameValue, currentUsername, dispatch]);

  // Handle username update
  const handleUsernameUpdate = useCallback(async ({ username }) => {
    const trimmedUsername = username.trim();
    const normalizedNewUsername = normalizeValue(trimmedUsername);
    const normalizedCurrentUsername = normalizeValue(currentUsername);

    if (isCheckingUsername) {
      return;
    }

    if (!trimmedUsername || normalizedNewUsername === normalizedCurrentUsername) {
      await SwalHandled.showError('Error', 'Please enter a different username before submitting.');
      return;
    }

    if (isUsernameAvailable === false) {
      await SwalHandled.showError(
        'Error',
        'This username is not available. Please choose another one.'
      );
      return;
    }

    try {
      const result = await dispatch(updateUserAction({ username: trimmedUsername }));

      if (!result?.success) {
        throw new Error(result?.error || result?.message || 'Failed to update username.');
      }

      await dispatch(fetchUser());

      if (isCreator) {
        await dispatch(fetchCreator(trimmedUsername));
      }

      setIsUsernameAvailable(null);
      form.reset({ username: '' });

      showManualSuccessPushToast(
        'Updated!',
        result?.message || 'Username updated successfully.',
        user
      );
    } catch (error) {
      await SwalHandled.showError(
        'Error',
        error.response?.data?.message ||
        error.message ||
        'Failed to update username. Please try again.'
      );
    }
  }, [dispatch, form, isCheckingUsername, isCreator, isUsernameAvailable]);

  const handleDeleteAccount = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeleteConfirmationText('');
  }, []);

  const normalizedDeleteConfirmation = useMemo(
    () => normalizeValue(deleteConfirmationText),
    [deleteConfirmationText]
  );
  const isDeleteConfirmationValid = normalizedDeleteConfirmation === DELETE_CONFIRMATION_WORD;


  const userEmail = user?.email;

  const handleConfirmDelete = useCallback(async () => {
    if (!isDeleteConfirmationValid || !userEmail) {
      return;
    }

    try {
      const result = await dispatch(
        deleteUserAction(userEmail, () => {
          // Clear all storage
          clearLocalStorage();
          clearSessionStorage();
          navigate.toLogin();
        })
      );

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to delete account. Please try again.');
      }

      await SwalHandled.showSuccess(
        'Deleted',
        'Your account has been deleted successfully.'
      );
    } catch (error) {
      await SwalHandled.showError(
        'Error',
        error.message || 'Failed to delete account. Please try again.'
      );
      return;
    }

    setIsDeleteModalOpen(false);
    setDeleteConfirmationText('');
  }, [dispatch, isDeleteConfirmationValid, navigate, userEmail]);

  const handleDeleteModalClick = useCallback((event) => {
    if (event.target.id === 'delete-modal') {
      handleCancelDelete();
    }
  }, [handleCancelDelete]);

  const handleFormSubmit = useCallback(async (values) => {
    const isValid = await validateAndFocus();
    if (!isValid) {
      return;
    }
    await handleUsernameUpdate(values);
  }, [handleUsernameUpdate, validateAndFocus]);

  if (!isLoggedIn) {
    return null;
  }

  const usernameError = shouldShowError('username');
  const showAvailabilitySuccess = !usernameError && isUsernameAvailable === true;
  const showAvailabilityError = !usernameError && isUsernameAvailable === false;

  const isSubmitDisabled =
    form.formState.isSubmitting ||
    isCheckingUsername ||
    !form.formState.isValid ||
    showAvailabilityError;


  return (
    <div className="modern-edit-username-page">
      <ModernHeader
        customTitle="Account"
        showButtonBorder={false}
        hideSearchIcon={true}
        showAvatar={false}
        showBackButton={true}
      />


      <main className="modern-edit-username-main">
        <div className="modern-edit-username-form">
          <Form {...form}>
            <form
              className="modern-edit-username-section"
              autoComplete="off"
              onSubmit={form.handleSubmit(handleFormSubmit)}
            >
              <div className="modern-edit-username-field">
                <label className="modern-edit-username-label" htmlFor="current-username">
                  Current Username
                </label>
                <div className="modern-edit-username-input-container">
                  <span className="modern-edit-username-prefix">@</span>
                  <input
                    id="current-username"
                    type="text"
                    value={currentUsername}
                    disabled
                    className="modern-edit-username-input modern-edit-username-input-disabled"
                    aria-label="Current username"
                  />
                </div>
              </div>

              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem className="modern-edit-username-field">
                    <FormLabel className="modern-edit-username-label" htmlFor="new-username">
                      New Username
                    </FormLabel>
                    <FormControlWithFocus fieldName="username">
                      <div className="modern-edit-username-input-container">
                        <span className="modern-edit-username-prefix modern-edit-username-prefix-active">@</span>
                        <input
                          id="new-username"
                          type="text"
                          placeholder="Enter new username"
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="none"
                          spellCheck="false"
                          maxLength={30}
                          className={`modern-edit-username-input modern-edit-username-input-active ${usernameError || showAvailabilityError ? 'invalid' : ''
                            } ${showAvailabilitySuccess ? 'valid' : ''}`}
                          disabled={form.formState.isSubmitting}
                          {...field}
                          onChange={(event) =>
                            handleInputChange(field, event.target.value, sanitizeUsernameInput)
                          }
                        />
                        {isCheckingUsername && (
                          <div className="modern-edit-username-checking">
                            <div className="modern-edit-username-spinner" />
                          </div>
                        )}
                        {showAvailabilitySuccess && (
                          <div className="modern-edit-username-available">
                            <i className="fa-solid fa-check" />
                          </div>
                        )}
                        {showAvailabilityError && (
                          <div className="modern-edit-username-unavailable">
                            <i className="fa-solid fa-times" />
                          </div>
                        )}
                      </div>
                    </FormControlWithFocus>

                    <FormMessageWithValidation
                      fieldName="username"
                      className="modern-edit-username-error-text"
                    />

                    <p className="modern-edit-username-help-text">
                      Username must be 3-30 characters (letters and numbers only)
                      {showAvailabilityError && (
                        <span className="modern-edit-username-error-text">
                          {' '}
                          - This username is not available
                        </span>
                      )}
                      {showAvailabilitySuccess && (
                        <span className="modern-edit-username-success-text"> - Available!</span>
                      )}
                    </p>
                  </FormItem>
                )}
              />

              <button
                type="submit"
                className="modern-edit-username-submit-btn"
                disabled={isSubmitDisabled}
              >
                {form.formState.isSubmitting ? 'Updating...' : 'Set Username'}
              </button>

              <div className="modern-edit-username-delete-section">
                <button
                  type="button"
                  className="modern-edit-username-delete-btn"
                  onClick={handleDeleteAccount}
                >
                  Delete Account
                </button>
              </div>
            </form>
          </Form>
        </div>
      </main>

      <div
        id="delete-modal"
        className={`delete-modal-overlay cm_bg_blur ${isDeleteModalOpen ? '' : 'hidden'}`}
        onClick={handleDeleteModalClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
      >
        <div className="delete-modal-content">
          <h3 id="delete-modal-title" className="delete-modal-title">
            Type 'delete' to permanently close your account.
          </h3>

          <div className="delete-modal-input-container">
            <input
              type="text"
              id="delete-confirmation"
              placeholder="Delete"
              value={deleteConfirmationText}
              onChange={(event) => setDeleteConfirmationText(event.target.value)}
              className="delete-modal-input"
              autoComplete="off"
            />
          </div>

          <div className="delete-modal-buttons">
            <button
              id="cancel-delete"
              onClick={handleCancelDelete}
              className="delete-modal-button delete-modal-cancel"
              type="button"
            >
              Cancel
            </button>
            <button
              id="confirm-delete"
              onClick={handleConfirmDelete}
              disabled={!isDeleteConfirmationValid}
              className={`delete-modal-button delete-modal-confirm ${!isDeleteConfirmationValid ? 'disabled' : ''}`}
              type="button"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const mapStateToProps = (state) => {
  return {
    user: state.user?.user,
  };
};

export default connect(mapStateToProps)(EditUsername);